/* eslint-disable no-unused-vars */
import { useSelector, useDispatch } from 'react-redux';
import { Typography } from '@mui/material';
import SectionLayout from 'components/common/SectionLayout';
import { Bid } from 'components';
import { isAdminSelector } from 'store/selectors';
import { generalActions } from 'store/actions';
import { isBidErrorShown, trimDecimal } from 'utils';
import * as S from './BiddingSection.styled';

/**
 * UA Budget Validation - Build error message for an active backend rule.
 * Only invoked when the rule is active ("CPI" | "Placements" | "Broad").
 * @see JIRA: UA Campaign Budget Validation
 */
const getBudgetRuleErrorMessage = (rule, minimum) => {
  const formattedMinimum = Number.isInteger(minimum) ? minimum.toString() : minimum.toFixed(2);

  switch ((rule || '').toLowerCase()) {
    case 'cpi':
      return `Budget must be at least 10x the Target CPI ($${formattedMinimum}).`;
    case 'broad':
      return `Broad campaigns (All Placements) require a minimum budget of $${formattedMinimum}.`;
    case 'placements':
    default:
      return `Based on your targeting (Placements x Countries), the minimum budget is $${formattedMinimum}.`;
  }
};

const getNativeBudgetErrorMessage = (budget, min, max) => {
  if (budget === '') {
    return 'This field is required!! The value cannot be less than the minimum';
  }

  if (budget < min || budget === '.') {
    return `Budget must be at least $${min}`;
  }

  if (budget > max) {
    return 'Your budget is too high';
  }

  return '';
};

/**
 * Daily Budget input with UA Budget Validation support.
 *
 * `budgetRuleError` is true ONLY when the backend returned an active rule
 * (CPI / Placements / Broad) and the user's budget is below the rule minimum.
 * For legacy campaigns ("LegacyMinimum") or no rule ("None") it stays false and
 * we fall back to the original native min/max error behavior.
 */
export const DailyBudgetValue = ({
  value,
  isOpen,
  minDailyBudget,
  maxDailyBudget,
  minimumBudget = 0,
  applicableRule = 'None',
  budgetRuleError = false,
}) => {
  const dispatch = useDispatch();

  const isAdmin = useSelector(isAdminSelector);
  const isError = isOpen && isBidErrorShown(value, minDailyBudget, maxDailyBudget);
  const errorText = budgetRuleError
    ? getBudgetRuleErrorMessage(applicableRule, minimumBudget)
    : getNativeBudgetErrorMessage(value, minDailyBudget, maxDailyBudget);

  function handleBudgetChange(e) {
    const newBudget = trimDecimal(e.target.value);
    dispatch(
      generalActions.updateCurrentCampaignObj({
        propField: 'budget',
        newValue: newBudget,
      }),
    );
  }

  function handleBlur(e) {
    const currentBudget = e.target.value;
    if (currentBudget.startsWith('.')) {
      const formatedBudget = '0' + currentBudget;

      dispatch(
        generalActions.updateCurrentCampaignObj({
          propField: 'budget',
          newValue: trimDecimal(formatedBudget),
        }),
      );
    }
  }

  return (
    <SectionLayout
      leftSide={<Typography variant='subtitle2'>Daily Budget:</Typography>}
      rightSide={
        <S.BidContainer>
          <Bid.Big
            label=''
            error={isError || budgetRuleError || (!value && isAdmin)}
            errorMesage={errorText}
            value={value ?? ''}
            onChange={handleBudgetChange}
            onBlur={handleBlur}
          />
        </S.BidContainer>
      }
    />
  );
};
